import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowDownTrayIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  QueueListIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";

import DashboardLayout from "../../components/DashboardLayout";
import WeaveLoader from "../../components/common/WeaveLoader";
import TableRowDrawer from "../../components/data/TableRowDrawer";
import { ChangeComposer } from "./Changes";
import { useToast } from "../../contexts/ToastContext";
import { changeRequestsService } from "../../services/changeRequests";
import { publicDatasetsService } from "../../services/publicDatasets";
import { tablesService } from "../../services/tables";
import { getApiErrorMessage } from "../../utils/axios";
import {
  buildDiffRows,
  classifyCell,
  compareCellValues,
  formatTableSize,
  mergeSchema,
} from "../../utils/tableDiff";
import { useConfirmLeave } from "../../utils/useConfirmLeave";
import { sitePath } from "../../utils/urls";

const ROW_PAGE_SIZE = 100;
const APPROVED_TOOLTIP = "When you approve a row, you can instruct the site builder to reference approved rows.";
const SECURITY_TOOLTIP = "This was flagged by security review and can't be included or approved.";
const SPAM_TOOLTIP = "Flagged as spam, can still be approved.";

const pendingKey = (tableHash, rowIndex) => `${tableHash}:${rowIndex}`;

const rowsEqual = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const DataTablePanel = () => {
  const { error: toastError, success: toastSuccess } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { "*": rest } = useParams();
  const pathParts = (rest || "").split("/").filter(Boolean);
  const tableHash = pathParts[0];
  const rowIndex = pathParts[1] === "rows" ? pathParts[2] : undefined;
  const project = useSelector((state) => state.project.currentProject);
  const fromParam = searchParams.get("from") || "";
  const toParam = searchParams.get("to") || "";
  const commitRange = Boolean(fromParam && toParam);

  const [tables, setTables] = useState([]);
  const [tablesLoaded, setTablesLoaded] = useState(false);
  const [dataRef, setDataRef] = useState("draft");
  const [diffByTable, setDiffByTable] = useState({});
  const [repoMeta, setRepoMeta] = useState({});
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [replacePreview, setReplacePreview] = useState(null);
  const [replaceConfirming, setReplaceConfirming] = useState(false);
  const [tableActionOpen, setTableActionOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDescription, setEditDescription] = useState("");
  const [editModel, setEditModel] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showAllPreviewRows, setShowAllPreviewRows] = useState(false);
  const [datasets, setDatasets] = useState([]);
  const [showDatasets, setShowDatasets] = useState(false);
  const [tableRows, setTableRows] = useState([]);
  const [beforeRows, setBeforeRows] = useState(null);
  const [beforeSchema, setBeforeSchema] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState("");
  const [pageTableHash, setPageTableHash] = useState(tableHash);
  const [pendingEdits, setPendingEdits] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [formInclude, setFormInclude] = useState("valid");
  const [rowIndexes, setRowIndexes] = useState(null);
  const [serverByIndex, setServerByIndex] = useState({});
  const pendingRef = useRef(pendingEdits);
  pendingRef.current = pendingEdits;
  const pendingCount = Object.keys(pendingEdits).length;
  const approvedTooltipId = useId();
  const securityTooltipId = useId();
  const sourceTooltipId = useId();

  // Active load ref: commit `to`, or live/draft branch.
  const loadRef = commitRange ? toParam : dataRef;
  const showDiff = commitRange || dataRef === "draft";
  const beforeRef = commitRange ? fromParam : "main";

  if (tableHash !== pageTableHash) {
    setPageTableHash(tableHash);
    setPage(1);
    setTableRows([]);
    setBeforeRows(null);
    setBeforeSchema([]);
    setRowIndexes(null);
    setServerByIndex({});
    setFormInclude("valid");
    setSearch("");
    setSearchInput("");
    setSortColumn("");
    setSortDirection("asc");
    setRowsError("");
  }

  const leaveMessage = pendingCount === 1
    ? "Are you sure you want to leave? You'll lose 1 draft data change."
    : `Are you sure you want to leave? You'll lose ${pendingCount} draft data changes.`;
  useConfirmLeave({
    active: pendingCount > 0 && Boolean(project),
    basePath: project ? sitePath(project, "data/").replace(/\/$/, "") : "",
    message: leaveMessage,
    onLeave: () => setPendingEdits({}),
  });

  const enrichTable = useCallback((table) => {
    if (!table || table.source === "form") return table;
    const meta = repoMeta[table.name];
    const side = meta?.[loadRef === "draft" ? "draft" : "live"];
    if (!side) return table;
    return {
      ...table,
      row_count: side.row_count,
      size_bytes: side.size_bytes,
      schema: side.schema?.length ? side.schema : table.schema,
    };
  }, [repoMeta, loadRef]);

  const displayTables = useMemo(
    () => tables.map(enrichTable),
    [tables, enrichTable],
  );

  const selected = useMemo(() => {
    if (!displayTables.length) return null;
    if (tableHash) {
      return displayTables.find((item) => item.hash === tableHash || item.name === tableHash)
        || displayTables[0]
        || null;
    }
    return displayTables[0] || null;
  }, [displayTables, tableHash]);

  const loadMeta = useCallback(() => {
    if (!project) return;
    tablesService.meta(
      project.hash,
      (response) => {
        const byName = {};
        const diffs = {};
        for (const item of response.data.tables || []) {
          byName[item.name] = item;
          if (item.diff) diffs[item.name] = item.diff;
        }
        setRepoMeta(byName);
        if (!commitRange) setDiffByTable(diffs);
      },
      () => {
        setRepoMeta({});
        if (!commitRange) setDiffByTable({});
      },
    );
  }, [project, commitRange]);

  const loadTables = useCallback(() => {
    if (!project) return;
    setTablesLoaded(false);
    tablesService.list(
      project.hash,
      {},
      (response) => {
        const items = Array.isArray(response.data) ? response.data : response.data.results;
        setTables(items);
        setTablesLoaded(true);
      },
      () => setTablesLoaded(true),
    );
  }, [project]);

  const refreshCatalog = useCallback(() => {
    loadTables();
    loadMeta();
  }, [loadTables, loadMeta]);

  useEffect(loadTables, [loadTables]);
  useEffect(loadMeta, [loadMeta]);

  useEffect(() => {
    if (!project || !commitRange) return undefined;
    tablesService.diff(
      project.hash,
      { from: fromParam, to: toParam },
      (response) => {
        const byName = {};
        for (const table of response.data.tables || []) {
          byName[table.name] = table;
        }
        setDiffByTable(byName);
      },
      () => setDiffByTable({}),
    );
    return undefined;
  }, [project, commitRange, fromParam, toParam]);

  useEffect(() => {
    if (!project || !selected || (tableHash && selected.hash !== tableHash && selected.name !== tableHash)) {
      return;
    }
    if (!tableHash) {
      const query = searchParams.toString();
      navigate(
        `${sitePath(project, `data/${selected.hash}/`)}${query ? `?${query}` : ""}`,
        { replace: true },
      );
    }
  }, [project, selected, tableHash, navigate, searchParams]);

  const isFormTable = selected?.source === "form";
  const tableInclude = isFormTable && formInclude === "all" ? "all" : undefined;

  const loadTable = useCallback(() => {
    if (!project || !tableHash) return;
    setRowsLoading(true);
    setRowsError("");
    tablesService.get(
      project.hash,
      tableHash,
      { ref: loadRef, include: tableInclude },
      (response) => {
        const rows = response.data.data || [];
        const indexes = response.data.row_indexes || rows.map((_, index) => index);
        const pending = pendingRef.current;
        const byIndex = {};
        rows.forEach((row, index) => {
          byIndex[indexes[index]] = row;
        });
        setServerByIndex(byIndex);
        setRowIndexes(indexes);
        setTableRows(rows.map((row, index) => (
          pending[pendingKey(tableHash, indexes[index])]?.data ?? row
        )));
        setRowsLoading(false);
      },
      () => {
        setRowsError("Could not load rows.");
        setRowsLoading(false);
      },
    );
  }, [project, tableHash, loadRef, tableInclude]);

  useEffect(loadTable, [loadTable]);

  // Baseline rows for inline diff (main vs draft, or from vs to).
  useEffect(() => {
    if (!project || !tableHash || !showDiff) {
      setBeforeRows(null);
      setBeforeSchema([]);
      return undefined;
    }
    let cancelled = false;
    setBeforeRows(null);
    setBeforeSchema([]);
    tablesService.get(
      project.hash,
      tableHash,
      { ref: beforeRef, include: tableInclude },
      (response) => {
        if (cancelled) return;
        setBeforeRows(response.data.data || []);
        setBeforeSchema(response.data.schema || []);
      },
      () => {
        if (cancelled) return;
        // Table may be new on draft / to — treat as empty baseline.
        setBeforeRows([]);
        setBeforeSchema([]);
      },
    );
    return () => { cancelled = true; };
  }, [project, tableHash, showDiff, beforeRef, tableInclude]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!editOpen && !tableActionOpen) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape" || deleting || editSubmitting) return;
      setEditOpen(false);
      setTableActionOpen(false);
      setDeleteConfirm(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editOpen, tableActionOpen, deleting, editSubmitting]);

  const setLiveDraft = (ref) => {
    if (pendingCount && ref !== "draft") {
      if (!window.confirm(leaveMessage)) return;
      setPendingEdits({});
    }
    setDataRef(ref);
    setPage(1);
    if (commitRange) {
      const next = new URLSearchParams(searchParams);
      next.delete("from");
      next.delete("to");
      setSearchParams(next, { replace: true });
    }
  };

  const uploadCsv = (event) => {
    const file = event.target.files[0];
    if (file) {
      setShowAllPreviewRows(false);
      tablesService.uploadCsv(project.hash, file, (response) => setPreview(response.data));
    }
    event.target.value = "";
  };

  const downloadCsv = () => {
    if (!project || !selected) return;
    tablesService
      .downloadCsv(project.hash, selected.hash, selected.name, { ref: loadRef })
      .catch((err) => toastError(err?.message || "Could not download CSV"));
  };

  const replaceCsv = (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file || !project || !selected) return;
    tablesService.replaceCsv(
      project.hash,
      selected.hash,
      file,
      { dryRun: true },
      (response) => {
        setReplacePreview({
          file,
          row_count: response.data.row_count,
          schema: response.data.schema,
          diff: response.data.diff,
        });
      },
      (requestError) => toastError(getApiErrorMessage(requestError, "Could not preview replace.")),
    );
  };

  const confirmDelete = () => {
    if (!project || !selected || deleting) return;
    const removedHash = selected.hash;
    setDeleting(true);
    tablesService.remove(
      project.hash,
      removedHash,
      () => {
        setDeleting(false);
        setTableActionOpen(false);
        setDeleteConfirm(false);
        toastSuccess("Deleted from draft", 3000);
        loadMeta();
        tablesService.list(
          project.hash,
          {},
          (response) => {
            const items = Array.isArray(response.data) ? response.data : response.data.results;
            setTables(items);
            setTablesLoaded(true);
            if (items.some((item) => item.hash === removedHash)) {
              loadTable();
              return;
            }
            navigate(sitePath(project, items[0] ? `data/${items[0].hash}/` : "data/"));
          },
          () => setTablesLoaded(true),
        );
      },
      (requestError) => {
        setDeleting(false);
        toastError(getApiErrorMessage(requestError, "Could not delete that table."));
      },
    );
  };

  const submitEdit = (event) => {
    event.preventDefault();
    const text = editDescription.trim();
    if (!text || !project || editSubmitting) return;
    setEditSubmitting(true);
    changeRequestsService.create(
      project.hash,
      text,
      "form",
      () => {
        setEditSubmitting(false);
        setEditOpen(false);
        setEditDescription("");
        toastSuccess("Edit queued", 3000);
      },
      (requestError) => {
        setEditSubmitting(false);
        toastError(getApiErrorMessage(requestError, "Could not queue that edit."));
      },
      { agent_type: "composite", model_override: editModel },
    );
  };

  const confirmReplace = () => {
    if (!project || !selected || !replacePreview?.file || replaceConfirming) return;
    setReplaceConfirming(true);
    tablesService.replaceCsv(
      project.hash,
      selected.hash,
      replacePreview.file,
      {},
      (response) => {
        setReplacePreview(null);
        setReplaceConfirming(false);
        setPage(1);
        setSearch("");
        setSearchInput("");
        refreshCatalog();
        loadTable();
      },
      (requestError) => {
        setReplaceConfirming(false);
        toastError(getApiErrorMessage(requestError, "Could not replace table."));
      },
    );
  };

  const confirmUpload = () => {
    if (!project || !preview || importing) return;
    setImporting(true);
    tablesService.create(
      project.hash,
      {
        name: preview.name.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
        source: "csv_upload",
        schema: preview.schema,
        data: preview.data,
        source_row_count: preview.source_row_count || preview.row_count,
      },
      () => {
        setImporting(false);
        setPreview(null);
        refreshCatalog();
      },
      (requestError) => {
        setImporting(false);
        toastError(getApiErrorMessage(requestError, "Could not import table."));
      },
    );
  };

  const browseDatasets = () => {
    publicDatasetsService.list((response) => {
      setDatasets(Array.isArray(response.data) ? response.data : response.data.results);
      setShowDatasets(true);
    });
  };

  const queueRowEdit = (index, data) => {
    if (!tableHash) return;
    const key = pendingKey(tableHash, index);
    const baseline = serverByIndex[index];
    setPendingEdits((current) => {
      const next = { ...current };
      if (baseline != null && rowsEqual(baseline, data)) {
        delete next[key];
      } else {
        next[key] = { tableHash, rowIndex: index, data };
      }
      return next;
    });
    setTableRows((rows) => {
      const displayIndex = rowIndexes ? rowIndexes.indexOf(index) : index;
      if (displayIndex < 0) return rows;
      return rows.map((row, i) => (i === displayIndex ? data : row));
    });
  };

  const publishPending = () => {
    if (!project || !pendingCount || publishing) return;
    const byTable = {};
    Object.values(pendingEdits).forEach((edit) => {
      if (!byTable[edit.tableHash]) byTable[edit.tableHash] = [];
      byTable[edit.tableHash].push({ index: edit.rowIndex, data: edit.data });
    });
    const tableHashes = Object.keys(byTable);
    setPublishing(true);

    const publishNext = (offset) => {
      if (offset >= tableHashes.length) {
        setPendingEdits({});
        setPublishing(false);
        navigate(sitePath(project, "changes/"));
        return;
      }
      const hash = tableHashes[offset];
      tablesService.bulkUpdateRows(
        project.hash,
        hash,
        byTable[hash],
        () => publishNext(offset + 1),
        (requestError) => {
          setPublishing(false);
          toastError(getApiErrorMessage(requestError, "Could not publish queued changes."));
        },
      );
    };
    publishNext(0);
  };

  const displaySchema = useMemo(() => {
    if (!selected) return [];
    const base = showDiff && beforeSchema.length
      ? mergeSchema(beforeSchema, selected.schema)
      : (selected.schema || []);
    return base.filter((column) => column?.name && column.name !== "_meta");
  }, [selected, showDiff, beforeSchema]);

  const toggleFormApproved = (event, row) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isFormTable || dataRef !== "draft" || commitRange) return;
    const meta = row.data?._meta && typeof row.data._meta === "object" ? row.data._meta : {};
    if (meta.classification === "security") return;
    const next = {
      ...row.data,
      _meta: { ...meta, approved: !meta.approved },
    };
    queueRowEdit(row.index, next);
  };

  // Wait for both refs before coloring — live-first makes every row look deleted.
  const diffReady = showDiff && beforeRows != null && !rowsLoading;

  const displayRows = useMemo(() => {
    let rows;
    if (diffReady) {
      rows = buildDiffRows(beforeRows, tableRows);
    } else {
      rows = tableRows.map((data, index) => ({ change: "same", data, index }));
    }
    if (!rowIndexes) return rows;
    return rows.map((row) => (
      typeof row.index === "number"
        ? { ...row, index: rowIndexes[row.index] ?? row.index }
        : row
    ));
  }, [diffReady, beforeRows, tableRows, rowIndexes]);

  const filteredRows = useMemo(() => {
    const needle = search.toLowerCase();
    let rows = needle
      ? displayRows.filter((row) => Object.values(row.data || {}).some(
        (value) => String(value ?? "").toLowerCase().includes(needle),
      ))
      : displayRows;
    if (sortColumn) {
      rows = [...rows].sort((a, b) => compareCellValues(
        a.data?.[sortColumn],
        b.data?.[sortColumn],
        sortDirection,
      ));
    }
    return rows;
  }, [displayRows, search, sortColumn, sortDirection]);

  const rowCount = filteredRows.length;
  const pageRows = filteredRows.slice((page - 1) * ROW_PAGE_SIZE, page * ROW_PAGE_SIZE);
  const openRow = displayRows.find((row) => String(row.index) === String(rowIndex));

  const previewRows = preview?.data || preview?.preview || [];
  const visiblePreviewRows = showAllPreviewRows ? previewRows : previewRows.slice(0, 10);
  const totalPages = Math.max(1, Math.ceil(rowCount / ROW_PAGE_SIZE));
  const rangeStart = rowCount ? (page - 1) * ROW_PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(page * ROW_PAGE_SIZE, rowCount);

  const toggleSort = (columnName) => {
    if (sortColumn === columnName) {
      setSortDirection((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnName);
      setSortDirection("asc");
    }
    setPage(1);
  };

  const tableMetaLabel = (table, { includeSize = false } = {}) => {
    if (!table) return "";
    const parts = [];
    if (includeSize) {
      const size = formatTableSize(table.size_bytes);
      if (size) parts.push(size);
    }
    parts.push(`${(table.row_count || 0).toLocaleString()} rows`);
    parts.push(`${table.schema?.length || 0} cols`);
    return parts.join(" · ");
  };

  const toggleRef = commitRange ? "draft" : dataRef;
  const addedCols = new Set(
    diffReady && beforeSchema.length
      ? displaySchema.filter((col) => !(beforeSchema || []).some((b) => b.name === col.name)).map((c) => c.name)
      : [],
  );
  const deletedCols = diffReady && beforeSchema.length
    ? (beforeSchema || []).filter((col) => !(selected?.schema || []).some((a) => a.name === col.name))
    : [];

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col px-3 py-3 sm:px-5 lg:px-6">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-theme-secondary lg:flex-row">
          <aside className="flex w-full flex-shrink-0 flex-col border-b border-theme-secondary bg-theme-secondary lg:w-64 lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <div className="flex min-w-0 items-center gap-2">
                <h2 className="text-[11px] font-semibold uppercase tracking-wide text-theme-text opacity-50">
                  Tables
                </h2>
                {displayTables.length > 0 && (
                  <span className="text-[11px] text-theme-text opacity-40">{displayTables.length}</span>
                )}
              </div>
              <div className="flex flex-shrink-0 rounded-lg border border-theme-secondary bg-theme-main">
                <button
                  type="button"
                  onClick={() => setLiveDraft("main")}
                  className={`inline-flex items-center gap-1 rounded-l-lg px-2.5 py-1.5 text-[11px] font-semibold ${
                    toggleRef === "main" ? "bg-theme-primary text-theme-button" : "text-theme-text opacity-60 hover:opacity-100"
                  }`}
                >
                  <GlobeAltIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Live
                </button>
                <button
                  type="button"
                  onClick={() => setLiveDraft("draft")}
                  className={`inline-flex items-center gap-1 rounded-r-lg border-l border-theme-secondary px-2.5 py-1.5 text-[11px] font-semibold ${
                    toggleRef === "draft" ? "bg-theme-primary text-theme-button" : "text-theme-text opacity-60 hover:opacity-100"
                  }`}
                >
                  <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Draft
                </button>
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto px-3 pb-3 lg:min-h-0 lg:flex-1 lg:flex-col lg:gap-0.5 lg:overflow-y-auto lg:px-2 lg:pb-2">
              <Tooltip
                id={sourceTooltipId}
                place="right"
                className="z-50 !max-w-xs !text-left !text-xs !leading-snug"
              />
              {displayTables.map((table) => {
                const active = selected?.hash === table.hash;
                const tableQuery = searchParams.toString();
                const diff = showDiff ? diffByTable[table.name] : null;
                const isForm = table.source === "form";
                const SourceIcon = isForm ? InboxIcon : DocumentTextIcon;
                return (
                  <Link
                    key={table.hash}
                    to={`${sitePath(project, `data/${table.hash}/`)}${tableQuery ? `?${tableQuery}` : ""}`}
                    title={table.name}
                    className={`min-w-[10.5rem] flex-shrink-0 rounded-lg px-3 py-2.5 text-left transition lg:min-w-0 lg:w-full ${
                      active
                        ? "bg-theme-main shadow-sm"
                        : "hover:bg-black hover:bg-opacity-5"
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <strong className="min-w-0 flex-1 truncate text-[13px] font-semibold text-theme-text">
                        {table.name}
                      </strong>
                      {diff && (
                        <span className="flex shrink-0 gap-1 font-mono text-[10px] font-semibold">
                          <span className="text-emerald-600">+{diff.cells_added || 0}</span>
                          <span className="text-red-500">-{diff.cells_deleted || 0}</span>
                          <span className="text-amber-600">~{diff.cells_updated || 0}</span>
                        </span>
                      )}
                    </div>
                    <span className="mt-0.5 flex min-w-0 items-center gap-1 truncate text-[11px] text-theme-text opacity-50">
                      <SourceIcon
                        data-tooltip-id={sourceTooltipId}
                        data-tooltip-content={isForm ? "Website form" : "Uploaded CSV file"}
                        className="h-3 w-3 flex-shrink-0"
                        aria-hidden="true"
                      />
                      {tableMetaLabel(table)}
                    </span>
                  </Link>
                );
              })}
              {tablesLoaded && !displayTables.length && (
                <p className="hidden px-2 py-3 text-xs text-theme-text opacity-50 lg:block">
                  No tables yet.
                </p>
              )}
            </div>

            <div className="flex gap-2 border-t border-theme-secondary p-3 lg:flex-col">
              <label className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-xs font-semibold text-theme-text transition hover:opacity-80 lg:justify-start">
                <PlusIcon className="h-3.5 w-3.5 flex-shrink-0" />
                Upload CSV
                <input type="file" accept=".csv,text/csv" onChange={uploadCsv} className="hidden" />
              </label>
              <button
                type="button"
                onClick={browseDatasets}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-theme-primary transition hover:opacity-70 lg:justify-start"
              >
                Browse public datasets
              </button>
            </div>
          </aside>

          <section className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            {!tablesLoaded ? (
              <div className="flex flex-1 items-center justify-center py-16">
                <WeaveLoader size={64} />
              </div>
            ) : selected ? (
              <>
                <header className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b border-theme-secondary px-4 py-3 sm:px-5">
                  <div className="min-w-0">
                    <h1 className="truncate text-base font-bold text-theme-text" title={selected.name}>
                      {selected.name}
                    </h1>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                      <span className="text-theme-text opacity-50">{tableMetaLabel(selected, { includeSize: true })}</span>
                      {pendingCount > 0 && (
                        <span className="inline-flex items-center gap-1 font-semibold text-theme-primary">
                          <QueueListIcon className="h-3.5 w-3.5" aria-hidden="true" />
                          {pendingCount === 1 ? "1 queued change" : `${pendingCount} queued changes`}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
                    {isFormTable && (
                      <div className="flex flex-shrink-0 rounded-lg border border-theme-secondary bg-theme-main">
                        <button
                          type="button"
                          onClick={() => {
                            setFormInclude("valid");
                            setPage(1);
                          }}
                          className={`rounded-l-lg px-2.5 py-1.5 text-[11px] font-semibold ${
                            formInclude === "valid"
                              ? "bg-theme-primary text-theme-button"
                              : "text-theme-text opacity-60 hover:opacity-100"
                          }`}
                        >
                          Valid
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormInclude("all");
                            setPage(1);
                          }}
                          className={`rounded-r-lg border-l border-theme-secondary px-2.5 py-1.5 text-[11px] font-semibold ${
                            formInclude === "all"
                              ? "bg-theme-primary text-theme-button"
                              : "text-theme-text opacity-60 hover:opacity-100"
                          }`}
                        >
                          All
                        </button>
                      </div>
                    )}
                    <div className="relative min-w-0 flex-1 sm:w-56 sm:flex-none">
                      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-theme-text opacity-40" />
                      <input
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        className="w-full rounded-lg border border-theme-secondary bg-theme-main py-2 pl-9 pr-3 text-sm text-theme-text placeholder:opacity-40"
                        placeholder="Search rows..."
                      />
                    </div>
                    <button
                      type="button"
                      onClick={downloadCsv}
                      title="Download"
                      aria-label="Download"
                      className="inline-flex items-center justify-center rounded-lg border border-theme-secondary p-2 text-theme-text transition hover:bg-theme-secondary"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteConfirm(false);
                        setTableActionOpen(true);
                      }}
                      title="Delete or replace"
                      aria-label="Delete or replace table"
                      className="inline-flex items-center justify-center rounded-lg border border-theme-secondary p-2 text-theme-text transition hover:bg-theme-secondary"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditDescription("");
                        setEditModel(project?.model_override || "");
                        setEditOpen(true);
                      }}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-theme-secondary px-3 py-2 text-sm font-semibold text-theme-text transition hover:bg-theme-secondary"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                      New edit
                    </button>
                    <button
                      type="button"
                      onClick={publishPending}
                      disabled={!pendingCount || publishing}
                      className="inline-flex shrink-0 items-center rounded-lg bg-theme-primary px-3 py-2 text-sm font-semibold text-theme-button disabled:opacity-50"
                    >
                      {publishing ? "Publishing..." : `Publish (${pendingCount})`}
                    </button>
                  </div>
                </header>

                <div className="min-h-0 flex-1 overflow-auto">
                  {isFormTable && (
                    <>
                      <Tooltip
                        id={approvedTooltipId}
                        place="bottom"
                        className="z-50 !max-w-xs !text-left !text-xs !leading-snug"
                      />
                      <Tooltip
                        id={securityTooltipId}
                        place="bottom"
                        className="z-50 !max-w-xs !text-left !text-xs !leading-snug"
                      />
                    </>
                  )}
                  <div className="min-w-max">
                    <div className="sticky top-0 z-10 flex border-b border-theme-secondary bg-theme-secondary">
                      {isFormTable && (
                        <div className="flex w-20 flex-shrink-0 items-center justify-center px-2 py-2.5">
                          <span
                            data-tooltip-id={approvedTooltipId}
                            data-tooltip-content={APPROVED_TOOLTIP}
                            className="cursor-help border-b border-dashed border-current text-[11px] font-semibold uppercase tracking-wide text-theme-text opacity-60"
                          >
                            Approved
                          </span>
                        </div>
                      )}
                      {displaySchema.map((column) => {
                        const sorted = sortColumn === column.name;
                        const isAdded = addedCols.has(column.name);
                        return (
                          <button
                            key={column.name}
                            type="button"
                            onClick={() => toggleSort(column.name)}
                            title={column.name}
                            className={`flex w-44 flex-shrink-0 items-center gap-1 truncate px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide transition hover:opacity-100 ${
                              isAdded ? "bg-emerald-500/15 text-emerald-700 opacity-100" : "text-theme-text opacity-60"
                            }`}
                          >
                            <span className="truncate">{column.name}</span>
                            {sorted && (
                              sortDirection === "asc"
                                ? <ChevronUpIcon className="h-3 w-3 flex-shrink-0" />
                                : <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}
                      {deletedCols.map((column) => (
                        <div
                          key={`del-${column.name}`}
                          title={column.name}
                          className="flex w-44 flex-shrink-0 items-center truncate bg-red-500/10 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-red-600 line-through"
                        >
                          {column.name}
                        </div>
                      ))}
                    </div>
                    {rowsError ? (
                      <div className="p-10 text-center text-sm text-theme-text opacity-50">{rowsError}</div>
                    ) : pageRows.length ? pageRows.map((row) => {
                      const isDeleted = row.change === "deleted";
                      const isAdded = row.change === "added";
                      const showCellDiff = diffReady && row.change !== "same";
                      const meta = row.data?._meta && typeof row.data._meta === "object"
                        ? row.data._meta
                        : {};
                      const classification = meta.classification || "";
                      const approved = Boolean(meta.approved);
                      const isSecurity = classification === "security";
                      const isSpam = classification === "spam";
                      const baselineApproved = Boolean(
                        serverByIndex[row.index]?._meta
                        && typeof serverByIndex[row.index]._meta === "object"
                          ? serverByIndex[row.index]._meta.approved
                          : false,
                      );
                      const approvedChanged = approved !== baselineApproved;
                      const approvedCell = isFormTable ? (
                        <div
                          className={`flex w-20 flex-shrink-0 items-center justify-center px-2 py-2.5 ${
                            approvedChanged ? "bg-amber-400/25" : ""
                          }`}
                        >
                          {isSecurity ? (
                            <span
                              data-tooltip-id={securityTooltipId}
                              data-tooltip-content={SECURITY_TOOLTIP}
                              className="inline-flex text-red-600"
                            >
                              <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : (
                            <button
                              type="button"
                              disabled={isDeleted || dataRef !== "draft" || commitRange}
                              onClick={(event) => toggleFormApproved(event, row)}
                              title={approved ? "Approved" : (isSpam ? SPAM_TOOLTIP : "Not approved")}
                              data-tooltip-id={isSpam && !approved ? securityTooltipId : undefined}
                              data-tooltip-content={isSpam && !approved ? SPAM_TOOLTIP : undefined}
                              className={`inline-flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-40 ${
                                approved ? "text-emerald-600" : isSpam ? "text-amber-500" : "text-red-600"
                              }`}
                            >
                              {approved
                                ? <CheckIcon className="h-4 w-4" />
                                : isSpam
                                  ? <ExclamationTriangleIcon className="h-4 w-4" />
                                  : <XMarkIcon className="h-4 w-4" />}
                            </button>
                          )}
                        </div>
                      ) : null;
                      const inner = (
                        <>
                          {approvedCell}
                          {displaySchema.map((column) => {
                            const value = isDeleted ? undefined : row.data?.[column.name];
                            const oldValue = isAdded ? undefined : row.before?.[column.name];
                            const kind = showCellDiff ? classifyCell(oldValue, value) : null;
                            let cellClass = "";
                            if (kind === "added") cellClass = "bg-emerald-500/15";
                            else if (kind === "deleted") cellClass = "bg-red-500/10";
                            else if (kind === "updated") cellClass = "bg-amber-400/25";
                            const displayOld = oldValue ?? "";
                            const displayNew = value ?? "";
                            let body = displayNew;
                            if (kind === "updated") {
                              body = (
                                <span className="inline-flex max-w-full items-baseline gap-1.5 overflow-hidden">
                                  <span className="truncate line-through opacity-50">{String(displayOld)}</span>
                                  <span className="truncate font-medium">{String(displayNew)}</span>
                                </span>
                              );
                            } else if (kind === "deleted") {
                              body = String(displayOld);
                            }
                            return (
                              <div
                                key={column.name}
                                title={kind === "updated"
                                  ? `${displayOld} → ${displayNew}`
                                  : String(kind === "deleted" ? displayOld : displayNew)}
                                className={`w-44 flex-shrink-0 truncate px-4 py-2.5 text-xs text-theme-text ${cellClass} ${
                                  kind === "deleted" ? "line-through opacity-70" : ""
                                }`}
                              >
                                {body}
                              </div>
                            );
                          })}
                          {deletedCols.map((column) => (
                            <div
                              key={`del-${column.name}`}
                              className="w-44 flex-shrink-0 truncate bg-red-500/10 px-4 py-2.5 text-xs text-red-600 line-through opacity-70"
                            >
                              {row.before?.[column.name] ?? row.data?.[column.name] ?? ""}
                            </div>
                          ))}
                        </>
                      );
                      if (isDeleted || dataRef !== "draft" || commitRange) {
                        return (
                          <div
                            key={row.index}
                            className={`flex w-full border-b border-theme-secondary last:border-b-0 ${rowsLoading ? "opacity-60" : ""}`}
                          >
                            {inner}
                          </div>
                        );
                      }
                      return (
                        <Link
                          key={row.index}
                          to={sitePath(project, `data/${selected.hash}/rows/${row.index}/`)}
                          className={`flex w-full border-b border-theme-secondary text-left transition last:border-b-0 hover:bg-theme-secondary hover:opacity-90 ${rowsLoading ? "opacity-60" : ""}`}
                        >
                          {inner}
                        </Link>
                      );
                    }) : (
                      <div className="p-10 text-center text-sm text-theme-text opacity-50">
                        {rowsLoading
                          ? "Loading rows..."
                          : search
                            ? "No rows match your search."
                            : "This table has no rows yet."}
                      </div>
                    )}
                  </div>
                </div>

                {rowCount > 0 && (
                  <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-t border-theme-secondary px-4 py-2.5 text-xs text-theme-text opacity-60 sm:px-5">
                    <span>
                      {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of {rowCount.toLocaleString()}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={page <= 1}
                        onClick={() => setPage((current) => current - 1)}
                        className="rounded-lg border border-theme-secondary px-3 py-1.5 font-semibold disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <span className="min-w-[4rem] text-center">
                        {page} / {totalPages}
                      </span>
                      <button
                        type="button"
                        disabled={page >= totalPages}
                        onClick={() => setPage((current) => current + 1)}
                        className="rounded-lg border border-theme-secondary px-3 py-1.5 font-semibold disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-theme-secondary text-theme-primary">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375" />
                  </svg>
                </div>
                <h2 className="mt-4 text-base font-semibold text-theme-text">Add your first table</h2>
                <p className="mt-1 max-w-sm text-sm text-theme-text opacity-60">
                  Upload a CSV or choose a public dataset. Tables power your site pages and AI edits.
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  <label className="cursor-pointer rounded-lg bg-theme-primary px-4 py-2.5 text-sm font-semibold text-theme-button">
                    Upload CSV
                    <input type="file" accept=".csv,text/csv" onChange={uploadCsv} className="hidden" />
                  </label>
                  <button
                    type="button"
                    onClick={browseDatasets}
                    className="rounded-lg border border-theme-secondary px-4 py-2.5 text-sm font-semibold text-theme-text"
                  >
                    Browse public datasets
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {preview && createPortal(
        <div className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center">
          <div className="my-auto w-full max-w-3xl rounded-xl border border-theme-secondary bg-theme-main p-6 shadow-xl">
            <h2 className="text-lg font-bold text-theme-text">Preview and confirm schema</h2>
            <p className="mt-1 text-sm text-theme-text opacity-60">
              {preview.name} · {preview.row_count.toLocaleString()} rows
              {preview.truncated && preview.source_row_count
                ? ` (using first ${preview.row_count.toLocaleString()} of ${Number(preview.source_row_count).toLocaleString()})`
                : ""}
            </p>
            <div className="mt-5 overflow-x-auto rounded-lg border border-theme-secondary">
              <div className="flex min-w-max bg-theme-secondary">
                {preview.schema.map((column) => (
                  <div key={column.name} className="w-36 flex-shrink-0 truncate p-3 text-xs font-semibold text-theme-text" title={`${column.name} · ${column.type}`}>
                    {column.name}
                    <span className="font-normal opacity-50"> · {column.type}</span>
                  </div>
                ))}
              </div>
              {visiblePreviewRows.map((row, index) => (
                <div key={index} className="flex min-w-max border-t border-theme-secondary">
                  {Object.values(row).map((value, valueIndex) => (
                    <div key={valueIndex} className="w-36 flex-shrink-0 truncate p-3 text-xs text-theme-text" title={String(value ?? "")}>
                      {value}
                    </div>
                  ))}
                </div>
              ))}
              {previewRows.length > 10 && !showAllPreviewRows && (
                <button
                  type="button"
                  onClick={() => setShowAllPreviewRows(true)}
                  className="w-full border-t border-theme-secondary p-3 text-xs font-semibold text-theme-primary"
                >
                  Show all {previewRows.length.toLocaleString()} rows
                </button>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPreview(null)}
                disabled={importing}
                className="rounded-lg border border-theme-secondary px-4 py-2 text-sm text-theme-text disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmUpload}
                disabled={importing}
                className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button disabled:opacity-50"
              >
                {importing ? "Importing..." : "Import table"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {tableActionOpen && selected && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
          onClick={() => {
            if (deleting) return;
            setTableActionOpen(false);
            setDeleteConfirm(false);
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-theme-secondary bg-theme-main p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-theme-text">{selected.name}</h2>
                <p className="mt-1 text-sm text-theme-text opacity-60">
                  Delete this table, or replace its contents with a new CSV.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setTableActionOpen(false);
                  setDeleteConfirm(false);
                }}
                className="rounded-lg p-1 text-theme-text opacity-50 hover:opacity-100"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-5 space-y-2">
              <label className="flex cursor-pointer items-center justify-between rounded-xl border border-theme-secondary px-4 py-3 text-sm font-semibold text-theme-text transition hover:bg-theme-secondary">
                Replace contents
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(event) => {
                    replaceCsv(event);
                    setTableActionOpen(false);
                    setDeleteConfirm(false);
                  }}
                />
              </label>
              {deleteConfirm ? (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4">
                  <p className="text-sm text-theme-text">
                    Delete {selected.name}? This removes it from the draft site.
                  </p>
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(false)}
                      disabled={deleting}
                      className="rounded-lg border border-theme-secondary px-3 py-1.5 text-sm text-theme-text"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={confirmDelete}
                      disabled={deleting}
                      className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white"
                    >
                      {deleting ? "Deleting..." : "Delete table"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(true)}
                  className="w-full rounded-xl border border-red-500/40 px-4 py-3 text-left text-sm font-semibold text-red-600 transition hover:bg-red-500/10"
                >
                  Delete this table
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {editOpen && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
          onClick={() => {
            if (!editSubmitting) setEditOpen(false);
          }}
        >
          <div className="my-auto w-full max-w-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold text-theme-text">New edit</h2>
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                className="rounded-lg p-1 text-theme-text opacity-50 hover:opacity-100"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <ChangeComposer
              description={editDescription}
              modelOverride={editModel}
              onChange={setEditDescription}
              onModelChange={setEditModel}
              onSubmit={submitEdit}
              placeholder="Describe a data edit for your agent: change existing values, add rows from research, or add new fields and fill them in."
              disabled={editSubmitting}
              autoFocus
            />
          </div>
        </div>,
        document.body,
      )}

      {replacePreview && createPortal(
        <div className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center">
          <div className="my-auto w-full max-w-lg rounded-xl border border-theme-secondary bg-theme-main p-6 shadow-xl">
            <h2 className="text-lg font-bold text-theme-text">Confirm replace</h2>
            <p className="mt-1 text-sm text-theme-text opacity-60">
              Replace <span className="font-semibold opacity-80">{selected?.name}</span> with{" "}
              {replacePreview.row_count.toLocaleString()} rows
              {replacePreview.schema?.length ? ` · ${replacePreview.schema.length} columns` : ""}.
              This overwrites the current table.
            </p>
            <div className="mt-5 grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-lg bg-theme-secondary p-2.5">
                <strong className={`block text-base ${replacePreview.diff.cells_added ? "text-emerald-600" : "text-theme-text"}`}>
                  {replacePreview.diff.cells_added}
                </strong>
                <span className="text-theme-text opacity-50">Cells filled</span>
              </div>
              <div className="rounded-lg bg-theme-secondary p-2.5">
                <strong className={`block text-base ${replacePreview.diff.cells_updated ? "text-amber-600" : "text-theme-text"}`}>
                  {replacePreview.diff.cells_updated}
                </strong>
                <span className="text-theme-text opacity-50">Cells changed</span>
              </div>
              <div className={`rounded-lg p-2.5 ${replacePreview.diff.cells_deleted ? "border border-red-500/40 bg-red-500/10" : "bg-theme-secondary"}`}>
                <strong className={`block text-base ${replacePreview.diff.cells_deleted ? "text-red-500" : "text-theme-text"}`}>
                  {replacePreview.diff.cells_deleted}
                </strong>
                <span className={replacePreview.diff.cells_deleted ? "text-red-500" : "text-theme-text opacity-50"}>
                  Cells cleared
                </span>
              </div>
            </div>
            <p className="mt-3 text-xs text-theme-text opacity-50">
              Columns added: {replacePreview.diff.columns_added?.join(", ") || "none"}
              {" · "}
              updated: {replacePreview.diff.columns_updated?.join(", ") || "none"}
              {" · "}
              <span className={replacePreview.diff.columns_deleted?.length ? "font-semibold text-red-500 opacity-100" : ""}>
                columns deleted: {replacePreview.diff.columns_deleted?.join(", ") || "none"}
              </span>
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setReplacePreview(null)}
                disabled={replaceConfirming}
                className="rounded-lg border border-theme-secondary px-4 py-2 text-sm text-theme-text disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmReplace}
                disabled={replaceConfirming}
                className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button disabled:opacity-40"
              >
                {replaceConfirming ? "Replacing..." : "Replace table"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {showDatasets && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-xl border border-theme-secondary bg-theme-main p-6 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-theme-text">Public datasets</h2>
                <p className="mt-1 text-sm text-theme-text opacity-60">
                  Import a ready-made table into this site.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowDatasets(false)}
                className="rounded-lg px-2 py-1 text-sm text-theme-text opacity-50 hover:opacity-100"
              >
                Close
              </button>
            </div>
            <div className="mt-4 space-y-2">
              {datasets.length ? datasets.map((dataset) => (
                <button
                  key={dataset.hash}
                  type="button"
                  onClick={() => tablesService.create(
                    project.hash,
                    { name: dataset.slug.replace(/-/g, "_"), public_dataset: dataset.hash },
                    () => {
                      setShowDatasets(false);
                      refreshCatalog();
                    },
                  )}
                  className="w-full rounded-xl border border-theme-secondary p-4 text-left transition hover:bg-theme-secondary"
                >
                  <strong className="text-sm text-theme-text">{dataset.name}</strong>
                  <p className="mt-1 text-sm text-theme-text opacity-60">
                    {dataset.description || `${dataset.row_count.toLocaleString()} rows`}
                  </p>
                </button>
              )) : (
                <p className="text-sm text-theme-text opacity-60">No public datasets have been published yet.</p>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {rowIndex != null && (
        <TableRowDrawer
          table={selected}
          row={openRow}
          baseline={openRow ? serverByIndex[openRow.index] : null}
          readOnly={dataRef !== "draft" || commitRange}
          onSaved={queueRowEdit}
        />
      )}
    </>
  );
};

const Data = ({ currentUser }) => {
  return (
    <DashboardLayout
      title="Data Tables"
      currentUser={currentUser}
      fillHeight
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <DataTablePanel />
      </div>
    </DashboardLayout>
  );
};

export default Data;
